import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Send, Linkedin, Mail, AtSign } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import SectionLabel from '../components/SectionLabel';

const GitHubIcon = ({ size = 16, className = '' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22" />
  </svg>
);

const OrcidIcon = ({ size = 16, className = '' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 256 256" className={className} aria-hidden="true">
    <path fill="currentColor" d="M256 128c0 70.7-57.3 128-128 128S0 198.7 0 128 57.3 0 128 0s128 57.3 128 128z" />
    <path
      fill="#fff"
      d="M86.3 186.2H70.9V79.1h15.4v107.1zM108.9 79.1h41.6c39.6 0 57 28.3 57 53.6 0 27.5-21.5 53.6-56.8 53.6h-41.8V79.1zm15.4 93.3h24.5c34.9 0 42.9-26.5 42.9-39.7C191.7 111.2 178 93 148 93h-23.7v79.4zM88.7 56.8c0 5.5-4.5 10.1-10.1 10.1s-10.1-4.6-10.1-10.1c0-5.6 4.5-10.1 10.1-10.1s10.1 4.6 10.1 10.1z"
    />
  </svg>
);

const socialLinks = [
  { label: 'k.glauch@googlemail.com', href: 'mailto:k.glauch@googlemail.com', icon: Mail },
  { label: 'GitHub', href: 'https://github.com/KGmbH', icon: GitHubIcon, external: true },
  { label: 'LinkedIn', href: 'https://www.linkedin.com/in/kalle-glauch-85b994302/', icon: Linkedin, external: true },
  { label: 'ORCID', href: 'https://orcid.org/0000-0002-6356-9910', icon: OrcidIcon, external: true },
];

const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // 'success' | 'error' | null

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus(null);
    try {
      const response = await fetch('/.netlify/functions/sendEmail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
    setIsSubmitting(false);
  };

  const inputClass =
    'w-full rounded-md border border-line bg-white px-3.5 py-2.5 text-sm text-ink-900 placeholder-ink-400 transition-colors focus:border-navy-600 focus:outline-none focus:ring-1 focus:ring-navy-600';

  return (
    <div>
      <PageHeader eyebrow="Kalle Glauch" title={t('contact.title')} lead={t('contact.lead')} />

      <div className="mx-auto max-w-5xl px-6 py-14 md:px-10 md:py-16">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-5">
          {/* Form */}
          <motion.div
            className="md:col-span-3"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
          >
            <SectionLabel icon={Send}>{t('contact.formLabel')}</SectionLabel>
            <form onSubmit={handleSubmit} className="mt-5 space-y-4">
              <div>
                <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-ink-900">
                  {t('contact.form.name')}
                </label>
                <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required className={inputClass} />
              </div>
              <div>
                <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-ink-900">
                  {t('contact.form.email')}
                </label>
                <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className={inputClass} />
              </div>
              <div>
                <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-ink-900">
                  {t('contact.form.message')}
                </label>
                <textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows="5" className={inputClass} />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-navy-900 px-6 py-2.5 text-sm font-semibold text-[color:var(--on-navy)] transition-colors hover:bg-navy-800 disabled:opacity-60"
              >
                <Send size={16} strokeWidth={1.9} />
                {isSubmitting ? t('contact.form.sending') : t('contact.form.submit')}
              </button>
              {status && (
                <p className={`text-sm ${status === 'success' ? 'text-navy-700' : 'text-red-600'}`}>
                  {status === 'success' ? t('contact.form.successMessage') : t('contact.form.errorMessage')}
                </p>
              )}
            </form>
          </motion.div>

          {/* Connect */}
          <motion.div
            className="md:col-span-2"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.1 }}
          >
            <SectionLabel icon={AtSign}>{t('contact.socialLinks')}</SectionLabel>
            <ul className="mt-5 divide-y divide-line overflow-hidden rounded-md border border-line bg-beige-50">
              {socialLinks.map(({ label, href, icon: Icon, external }) => (
                <li key={label}>
                  <a
                    href={href}
                    {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                    className="flex items-center gap-3 px-5 py-3.5 text-sm text-ink-900 transition-colors hover:bg-beige-200 hover:text-navy-700"
                  >
                    <Icon size={16} className="flex-shrink-0 text-navy-600" />
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
